import { useLocation, useNavigate } from "react-router-dom";
import useAppContext from "../../hooks/useAppContext";
import {
  wrapperClass,
  testBarContentClass,
  messageBarMarginClass,
  notificationMessageBarClass,
  toolbarClass,
} from "./styles";
import {
  Button,
  MessageBar,
  MessageBarBody,
  MessageBarActions,
  ProgressBar,
  Overflow,
  Toolbar,
} from "@fluentui/react-components";
import { useMutation } from "@tanstack/react-query";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import useMessageContext from "../../hooks/useMessageContext";
import Feedback from "../Feedback/Feedback";
import { useEffect, useRef, useState } from "react";
import useAuthContext from "../../hooks/useAuthContext";
import { followConversation, unfollowByMessage } from "../../api/follow";
import useOfficeContext from "../../hooks/useOfficeContext";
import { testAPI } from "../../api/test";
import { pauseNotification } from "../../api/pause";
import { getEnvName } from "../../utils";
import { useFollowedConversations } from "../../contexts/FollowedConversationsContext";
import { useNavigationContext } from "../../contexts/navigationContext";
import { DismissRegular } from "@fluentui/react-icons";
import { iManageLoginModal } from "../../api/iManageAuth";
import { useToolbarItems, ToolbarItemRenderer, OverflowMenuButton } from "./toolbarItems";

const Header = () => {
  const { refetch, isRefetching, isFollowed } = useAppContext();
  const { tokenData } = useAuthContext();
  const msUserId = tokenData.oid;
  const appInsights = useAppInsightsContext();
  const location = useLocation();
  const { refetchFollowedConversations } = useFollowedConversations();
  const { itemIds } = useOfficeContext();
  const { message, handleCloseMessageBar, handleShowMessageBar, isPauseOff, showMessageBar } = useMessageContext();
  const navigate = useNavigate();
  const { setFromPage, fromPage } = useNavigationContext();
  const environment = getEnvName();

  const { mutateAsync: runTest, isPending: isTestPending } = useMutation({
    mutationFn: async () => testAPI(msUserId, itemIds),
  });

  const { mutateAsync: pauseMutate } = useMutation({
    mutationFn: async (duration: number) => pauseNotification(duration, msUserId),
    onSuccess: () => {
      handleShowMessageBar("Email pause off: You will now receive all emails in your inbox.", true);
    },
  });

  const navigateToPause = (buttonText: string, duration: string) => {
    setFromPage(true);
    navigate("/pause-notification", {
      state: { buttonText, title: "Pause Emails", duration },
    });
  };

  const { appInfo, cleariManageAuthUrl } = useAuthContext();
  const pauseExpiryDate = new Date(appInfo.pauseExpiry);

  const hasShownRef = useRef(false);

  useEffect(() => {
    const url = appInfo?.iManageAuthUrl;
    if (!url || hasShownRef.current) return;

    hasShownRef.current = true;

    const hostName = Office?.context?.mailbox?.diagnostics?.hostName ?? "";
    const email = Office?.context?.mailbox?.userProfile?.emailAddress ?? "";

    const iManageAuthUrl =
      `https://${url}` +
      `&login_hint=${encodeURIComponent(email)}` +
      `&auto_submit=true&remember_email=true`;

    iManageLoginModal(iManageAuthUrl, hostName, () => {
      cleariManageAuthUrl?.();
      hasShownRef.current = false;
    });
  }, [appInfo?.iManageAuthUrl, cleariManageAuthUrl]);

  useEffect(() => {
    const options: Intl.DateTimeFormatOptions = { weekday: "long" };
    let hours = pauseExpiryDate.getHours();
    const minutes = pauseExpiryDate.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "pm" : "am";
    hours = hours % 12;
    hours = hours ? hours : 12;

    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);

    let date;
    if (pauseExpiryDate.toDateString() === today.toDateString()) {
      date = "today";
    } else if (pauseExpiryDate.toDateString() === tomorrow.toDateString()) {
      date = "tomorrow";
    } else {
      date = pauseExpiryDate.toLocaleDateString("en-GB", options);
    }

    const dateFormatted = `${hours}:${minutes} ${ampm} ${date}`;

    if (pauseExpiryDate.getTime() > Date.now()) {
      handleShowMessageBar(`Incoming emails are paused until ${dateFormatted}`, false);
    }
  }, []);

  const [isOpenFeedback, setIsOpenFeedback] = useState(false);
  const showModal = () => setIsOpenFeedback(true);

  const messageBarIntent = isPauseOff ? "info" : "success";

  const items = useToolbarItems({
    isHomePage: location.pathname === "/",
    onRefresh: () => { refetch(); appInsights.trackEvent({ name: "Menu click: Refresh" }); },
    isRefetching,
    onBack: () => { if (fromPage) { navigate(-1); } else { navigate("/"); } },
    isFollowed,
    onFollow: () => {
      followConversation(msUserId, itemIds);
      setTimeout(() => { if (refetchFollowedConversations) { refetchFollowedConversations(); } }, 100);
    },
    onUnfollow: () => unfollowByMessage(msUserId, itemIds),
    onCatchUp: () => { setFromPage(true); navigate("/followedConversations"); },
    onFile: () => { appInsights.trackEvent({ name: "Menu click: File" }); setFromPage(true); navigate("/file"); },
    onSummarise: () => { appInsights.trackEvent({ name: "Menu click: Summarise" }); setFromPage(true); navigate("/messageSummary"); },
    onPauseNavigate: navigateToPause,
    onPauseOff: () => { appInsights.trackEvent({ name: "Menu click: Pause off" }); void pauseMutate(0); },
    showTest: environment === "Dev" || environment === "Local-Dev" || environment === "Test",
    onTest: () => void runTest(),
    isTestPending,
    onPriorityContacts: () => { appInsights.trackEvent({ name: "Menu click: Priority Contacts" }); setFromPage(true); navigate("/priority-contacts", { state: { buttonText: "Save", title: "Priority Contacts", key: Date.now() } }); },
    onFeedback: showModal,
    onAbout: () => { appInsights.trackEvent({ name: "Menu click: About" }); setFromPage(true); navigate("/about"); },
    onHelp: () => { appInsights.trackEvent({ name: "Menu click: Help" }); window.open("https://fileops.sharepoint.com/sites/Support", "_blank"); },
    trackEvent: (name: string) => appInsights.trackEvent({ name }),
  });
  const visibleItems = items.filter((item) => item.visible !== false);

  return (
    <div className={wrapperClass}>
      <Overflow>
        <Toolbar className={toolbarClass}>
          {visibleItems.map((item) => (
            <ToolbarItemRenderer key={item.id} item={item} />
          ))}
          <OverflowMenuButton items={visibleItems} />
        </Toolbar>
      </Overflow>

      {/* Test progress bar */}
      {isTestPending && (
        <MessageBar intent="info" className={messageBarMarginClass}>
          <MessageBarBody>
            <div className={testBarContentClass}>
              <span>Running test…</span>
              <ProgressBar />
            </div>
          </MessageBarBody>
        </MessageBar>
      )}

      {showMessageBar && (
        <MessageBar intent={messageBarIntent} className={notificationMessageBarClass}>
          <MessageBarBody>{message}</MessageBarBody>
          <MessageBarActions
            containerAction={
              <Button
                appearance="transparent"
                icon={<DismissRegular />}
                aria-label="Close"
                onClick={handleCloseMessageBar}
              />
            }
          />
        </MessageBar>
      )}

      <Feedback
        isOpenFeedback={isOpenFeedback}
        setIsOpenFeedback={setIsOpenFeedback}
        onFormSubmit={() => {
          handleShowMessageBar("Thank you for your feedback", false);
        }}
      />
    </div>
  );
};

export default Header;
